import { Add, Close, Edit } from '@mui/icons-material';
import {
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    FormControlLabel,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { IMaskInput } from 'react-imask';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useFetch } from '../../../hooks/useFecth';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';

const CommissionField = React.forwardRef(function CommissionField(props, ref) {
    const { onChange, value, ...other } = props;
    return <IMaskInput {...other} value={String(value)} inputRef={ref} onAccept={value => onChange(props.name, value)} />;
});

const isPercentual = is => {
    if (is) {
        return {
            mask: 'num%',
            unmask: true,
            blocks: {
                num: {
                    mask: Number,
                    scale: 2,
                    radix: ',',
                    min: 0,
                    max: 100,
                },
            },
        };
    } else {
        return {
            mask: 'R$ num',
            unmask: true,
            blocks: {
                num: {
                    mask: Number,
                    scale: 2,
                    thousandsSeparator: '.',
                    radix: ',',
                },
            },
        };
    }
};

const offerSchema = yup.object().shape({
    name: yup.string().required('Informe o nome'),
    description: yup.string(),
    offerType: yup.string().oneOf(['voucher', 'coupon']).required('Escolha o tipo da oferta.'),
    url: yup.string().when('offerType', {
        is: 'coupon',
        then: schema =>
            schema
                .trim()
                .required('Informe a URL do cupom.')
                .test('http-url', 'Informe uma URL válida (http ou https).', value => {
                    try {
                        const parsed = new URL(value);
                        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
                    } catch {
                        return false;
                    }
                }),
        otherwise: schema => schema.trim().notRequired(),
    }),
    code: yup.string().trim(),
    value: yup.number().typeError('Informe um valor numérico'),
    avaliableQty: yup.number().typeError('Informe um número'),
});

const statusOptions = [
    { label: 'Todos', value: 'all' },
    { label: 'Ativos', value: 'true' },
    { label: 'Inativos', value: 'false' },
];

const OFFER_FORM_ID = 'advertiser-offer-dialog-form';

const OfferDialog = ({ open, onClose, offer, onSuccess }) => {
    const { setErrors, enqueueSnackbar } = useErrors();
    const isEditing = Boolean(offer?.id);

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: offerSchema,
        initialValues: {
            name: offer?.name || '',
            label: offer?.label || '',
            description: offer?.description || '',
            privateDescription: offer?.privateDescription || '',
            value: offer?.value ?? '',
            avaliableQty: offer?.avaliableQty ?? '',
            rules: offer?.rules || '',
            isPercentual: offer?.isPercentual || false,
            expirationAt: offer?.expirationAt || '',
            status: typeof offer?.status === 'boolean' ? offer.status : true,
            offerType: offer?.isCoupon ? 'coupon' : 'voucher',
            url: offer?.url || '',
            code: offer?.code || '',
        },
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const expirationAt = values.expirationAt
                    ? values.expirationAt instanceof Date
                        ? values.expirationAt.toISOString()
                        : values.expirationAt
                    : null;

                const isCoupon = values.offerType === 'coupon';
                const payload = {
                    name: values.name,
                    label: values.label,
                    description: values.description,
                    privateDescription: values.privateDescription,
                    rules: values.rules,
                    value: values.value !== '' && values.value != null ? Number(values.value) : undefined,
                    avaliableQty: values.avaliableQty !== '' && values.avaliableQty != null ? Number(values.avaliableQty) : undefined,
                    isPercentual: values.isPercentual,
                    status: values.status,
                    offerChannel: 'virtual',
                    isCoupon,
                    isVoucher: !isCoupon,
                    isCashback: isEditing ? Boolean(offer.isCashback) : false,
                    url: isCoupon ? values.url.trim() : null,
                    externalUrl: isCoupon,
                    code: isCoupon ? values.code.trim() || null : null,
                    expirationAt,
                };

                if (offer?.id) {
                    await server.put(`/advertiser/offers/${offer.id}`, payload);
                    enqueueSnackbar(`Oferta ${values.name} alterada.`);
                } else {
                    await server.post('/advertiser/offers', payload);
                    enqueueSnackbar(`Oferta ${values.name} adicionada.`);
                }
                await onSuccess();
                onClose();
                setSubmitting(false);
            } catch (error) {
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    return (
        <Dialog key={offer?.id ?? 'nova'} open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <DialogTitle>{offer ? 'Editando oferta' : 'Cadastrar oferta'}</DialogTitle>
                <IconButton size="small" onClick={onClose} sx={{ mr: 2 }}>
                    <Close />
                </IconButton>
            </Stack>
            <Divider />
            <DialogContent dividers>
                <Stack rowGap={2} pt={2} component="form" id={OFFER_FORM_ID} onSubmit={formik.handleSubmit}>
                    <Stack spacing={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Tipo de oferta
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            color="primary"
                            value={formik.values.offerType}
                            onChange={(_event, next) => {
                                if (!next) return;
                                formik.setFieldValue('offerType', next);
                                if (next !== 'coupon') {
                                    formik.setFieldValue('url', '');
                                    formik.setFieldValue('code', '');
                                    formik.setFieldError('url', undefined);
                                }
                            }}
                        >
                            <ToggleButton value="voucher">Voucher</ToggleButton>
                            <ToggleButton value="coupon">Cupom</ToggleButton>
                        </ToggleButtonGroup>
                        {formik.touched.offerType && formik.errors.offerType ? (
                            <Typography variant="caption" color="error">
                                {formik.errors.offerType}
                            </Typography>
                        ) : null}
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            {formik.values.offerType === 'voucher'
                                ? 'Voucher: o assinante resgata no app e recebe um código exclusivo gerado pelo sistema. Ele apresenta esse código na sua loja (ou conforme as regras da oferta) para você validar.'
                                : 'Cupom: o assinante usa um link da oferta — e, se informado, um código fixo — para aplicar o desconto no seu site ou checkout online.'}
                        </Typography>
                    </Stack>
                    {formik.values.offerType === 'coupon' && (
                        <>
                            <TextField
                                required
                                fullWidth
                                label="URL do cupom"
                                name="url"
                                value={formik.values.url}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.url && Boolean(formik.errors.url)}
                                helperText={
                                    (formik.touched.url && formik.errors.url) ||
                                    'Link para onde o assinante será direcionado ao pegar o cupom.'
                                }
                            />
                            <TextField
                                fullWidth
                                label="Código do cupom"
                                name="code"
                                value={formik.values.code}
                                onChange={formik.handleChange}
                                helperText="Opcional. Código promocional fixo que o assinante digita no checkout."
                            />
                        </>
                    )}
                    <TextField
                        onChange={formik.handleChange}
                        label="Titulo da oferta"
                        name="name"
                        value={formik.values.name}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />
                    <TextField
                        onChange={formik.handleChange}
                        label="Subtitulo da oferta"
                        name="label"
                        title="O subtitulo é o texto que aparece abaixo do titulo da oferta"    
                        value={formik.values.label}
                        error={formik.touched.label && Boolean(formik.errors.label)}
                        helperText={formik.touched.label && formik.errors.label}
                    />

                    <TextField
                        rows={3}
                        multiline
                        onChange={formik.handleChange}
                        label="Descrição da oferta"
                        name="description"
                        value={formik.values.description}
                    />

                    <TextField
                        rows={2}
                        multiline
                        onChange={formik.handleChange}
                        label="Regras gerais"
                        name="rules"
                        value={formik.values.rules}
                    />

                    <TextField
                        type="number"
                        onChange={formik.handleChange}
                        label="Quantidade disponível"
                        name="avaliableQty"
                        value={formik.values.avaliableQty}
                    />

                    <TextField
                        fullWidth
                        label="Valor do desconto"
                        name="value"
                        value={String(formik.values.value)}
                        onChange={formik.setFieldValue}
                        error={formik.touched.value && Boolean(formik.errors.value)}
                        helperText={
                            (formik.touched.value && formik.errors.value) ||
                            (formik.values.isPercentual
                                ? 'Com % marcado, o valor do campo é a porcentagem de desconto (ex.: 10 = 10%).'
                                : 'Sem % marcado, o valor do campo é o desconto em reais (ex.: 15 = R$ 15,00).')
                        }
                        FormHelperTextProps={
                            !(formik.touched.value && formik.errors.value)
                                ? { sx: { color: 'text.secondary', fontSize: '0.75rem' } }
                                : undefined
                        }
                        InputProps={{
                            inputProps: { ...isPercentual(formik.values.isPercentual) },
                            inputComponent: CommissionField,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <FormControlLabel
                                        sx={{
                                            m: 0,
                                            p: 0,
                                            '& .MuiFormControlLabel-label': {
                                                fontWeight: formik.values.isPercentual ? 700 : 400,
                                            },
                                        }}
                                        control={
                                            <Checkbox
                                                checked={formik.values.isPercentual}
                                                onChange={e => formik.setFieldValue('isPercentual', e.target.checked)}
                                            />
                                        }
                                        label="%"
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {Boolean(offer?.id) && (
                        <>
                            <DateTimePicker
                                value={formik.values.expirationAt ? new Date(formik.values.expirationAt) : null}
                                format="dd/MM/yyyy HH:mm"
                                onChange={e => formik.setFieldValue('expirationAt', e)}
                                name="expirationAt"
                                label="Expira em"
                                slotProps={{
                                    textField: {
                                        error: formik.touched.expirationAt && Boolean(formik.errors.expirationAt),
                                        helperText: formik.touched.expirationAt && formik.errors.expirationAt,
                                        fullWidth: true,
                                    },
                                    field: { clearable: true, onClear: () => formik.setFieldValue('expirationAt', '') },
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formik.values.status}
                                        onChange={e => formik.setFieldValue('status', e.target.checked)}
                                    />
                                }
                                label="Status da oferta"
                            />
                        </>
                    )}
                    <Divider> Dados internos </Divider>
                    <TextField
                        rows={3}
                        multiline
                        onChange={formik.handleChange}
                        label="Observações (Privado)"
                        name="privateDescription"
                        value={formik.values.privateDescription}
                    />
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button type="submit" form={OFFER_FORM_ID} variant="contained" disabled={formik.isSubmitting}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default function AdvertiserOffers() {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const { search, input, handleChange: handleSearchChange, handleReset } = useSearchTextInput();
    const perPage = 10;

    const params = useMemo(
        () => ({
            params: {
                perPage,
                name: search || undefined,
                status: statusFilter === 'all' ? undefined : statusFilter,
            },
        }),
        [perPage, search, statusFilter],
    );

    const { data, isLoading, mutate } = useFetch('/advertiser/offers', params);


    const handleCloseDialog = () => {
        setEditing(null);
        setDialogOpen(false);
    };

    const openCreateDialog = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const offers = data?.rows || [];

    return (
        <Stack spacing={3} position="relative">
            <Typography variant="h4" fontWeight="bold">
                Ofertas
            </Typography>
            <Card>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Buscar por nome"
                                value={input}
                                onChange={event => handleSearchChange(event.target.value)}
                                InputProps={{
                                    endAdornment: input ? (
                                        <Button size="small" onClick={handleReset}>
                                            Limpar
                                        </Button>
                                    ) : null,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                select
                                label="Status"
                                fullWidth
                                value={statusFilter}
                                onChange={event => setStatusFilter(event.target.value)}
                            >
                                {statusOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog} fullWidth sx={{ maxWidth: { md: 280 } }}>
                                Cadastrar oferta
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Quantidade</TableCell>
                                <TableCell>Valor</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {offers?.map(offer => (
                                <TableRow key={offer.id} hover>
                                    <TableCell>
                                        <Typography fontWeight="bold">{offer.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {offer.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={offer.status ? 'Ativa' : 'Inativa'}
                                            color={offer.status ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {offer.isCoupon && <Chip label="Cupom" size="small" color="primary" variant="outlined" />}
                                            {offer.isVoucher && <Chip label="Voucher" size="small" variant="outlined" />}
                                            {offer.isCashback && <Chip label="Cashback" size="small" variant="outlined" />}
                                            {!offer.isCoupon && !offer.isVoucher && !offer.isCashback && (
                                                <Typography variant="caption" color="text.secondary">
                                                    —
                                                </Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{offer.avaliableQty ?? '--'}</TableCell>
                                    <TableCell>{offer.value ? `R$ ${Number(offer.value).toFixed(2)}` : '--'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setEditing(offer);
                                                setDialogOpen(true);
                                            }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {offers?.length === 0 && !isLoading && (
                        <Typography variant="body2" color="text.secondary" mt={2}>
                            Nenhuma oferta encontrada.
                        </Typography>
                    )}

                </CardContent>
            </Card>

            <Fab color="primary" sx={{ position: 'fixed', bottom: 32, right: 32 }} onClick={openCreateDialog} aria-label="Cadastrar oferta">
                <Add />
            </Fab>

            <OfferDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                offer={editing}
                onSuccess={() => mutate()}
            />
        </Stack>
    );
}

